import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const About = ({ data }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, threshold: 0.3 });

    return (
        <section id="about" className="py-20" style={{ backgroundColor: '#FAEDCD' }}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mx-auto"
                >
                    <div className="text-center mb-12">
                        <motion.h2
                            className="text-3xl sm:text-4xl font-bold text-foreground mb-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            About Me
                        </motion.h2>
                        <motion.div
                            className="w-20 h-1 bg-accent mx-auto"
                            initial={{ scaleX: 0 }}
                            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        />
                    </div>

                    <div className="max-w-4xl mx-auto">
                        {/* About Text */}
                        <motion.div
                            className="text-center space-y-8"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            <p className="text-foreground/80 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto">
                                {data.description}
                            </p>

                            <p className="text-foreground/70 leading-relaxed max-w-3xl mx-auto">
                                {data.detailedDescription}
                            </p>

                            {/* Quick Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
                                {data.stats.map((stat, index) => (
                                    <motion.div
                                        key={stat.label}
                                        className="text-center p-4 rounded-xl bg-white/50 border border-white/20 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                                        transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                                        whileHover={{ y: -5 }}
                                    >
                                        <div className="text-3xl font-bold text-primary mb-2">
                                            {stat.value}
                                        </div>
                                        <div className="text-foreground/60 text-xs sm:text-sm uppercase tracking-wider font-medium">
                                            {stat.label}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Interests */}
                        <motion.div
                            className="mt-20"
                            initial={{ opacity: 0, y: 30 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                            transition={{ duration: 0.8, delay: 0.7 }}
                        >
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-semibold text-foreground mb-4">
                                    Interests & Hobbies
                                </h3>
                                <div className="w-12 h-1 bg-primary/20 mx-auto rounded-full" />
                            </div>

                            <div className="flex flex-wrap justify-center gap-3">
                                {data.interests.map((interest, index) => (
                                    <motion.span
                                        key={interest}
                                        className="px-4 py-2 text-foreground/80 rounded-full text-sm font-medium transition-all duration-200 cursor-default border hover:border-accent"
                                        style={{
                                            backgroundColor: '#FAEDCD',
                                            borderColor: '#A67C52'
                                        }}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                                        transition={{
                                            opacity: { duration: 0.4, delay: 0.8 + index * 0.1 },
                                            scale: { duration: 0.2 }
                                        }}
                                        whileHover={{
                                            scale: 1.05,
                                            backgroundColor: 'rgb(var(--accent))',
                                            color: 'rgb(var(--accent-foreground))'
                                        }}
                                    >
                                        {interest}
                                    </motion.span>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default About;